
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { AccountInformationDetails } from '@/@types/responses/miscellaneous/AccountInformationDetails'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import LockingButton from '@/components/shared/LockingButton.vue'
import { MiscellaneousService } from '@/services/miscellaneous.service'
import { AccountInformationMessage } from '@/@types/messages/AccountInformationMessage'
import { LOCAL_STORAGE_CONSTANTS } from '@/constants/localstorage.contstants'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { StateService } from '@/services/states.service'

export default defineComponent({
  name: 'MyAccount',
  components: {
    Dropdown,
    LoadingPulse,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  setup() {
    const miscellaneousService = inject(PROVIDER_CONSTANTS.miscellaneousService) as MiscellaneousService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      miscellaneousService,
      stateService,
      toast
    }
  },
  data() {
    let accountInformation: AccountInformationDetails|undefined
    let unchangedAccountInformation: AccountInformationDetails|undefined

    return {
      accountInformation,
      deleting: false,
      loading: true,
      saving: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedAccountInformation
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.accountInformation
      const b = this.unchangedAccountInformation

      return (a?.firstName !== b?.firstName ||
              a?.lastName !== b?.lastName ||
              a?.phoneNumber !== b?.phoneNumber ||
              a?.addressLine1 !== b?.addressLine1 ||
              a?.addressLine2 !== b?.addressLine2 ||
              a?.city !== b?.city ||
              a?.stateId !== b?.stateId ||
              a?.postalCode !== b?.postalCode) && (a?.firstName.trim() !== '' && a?.lastName.trim() !== '')
    }
  },
  mounted() {
    this.getStates().then(() => {
      this.getAccountInformation()
    })
  },
  methods: {
    getAccountInformation(): Promise<void> {
      return this.miscellaneousService.getAccountInformation().then(accountInformation => {
        this.accountInformation = accountInformation
        this.unchangedAccountInformation = JSON.parse(JSON.stringify(accountInformation))
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your account information. Try reloading the page.')
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onDeleteAccountClicked(): void {
      this.deleting = true

      this.miscellaneousService.deleteAccount().then(() => {
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.accessToken, '')
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.isEmployee, 'false')
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.refreshToken, '')
        window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CONSTANTS.eventName))
        
        this.deleting = false

        this.$router.push('/')
        
        this.toast.success('Your account was deleted successfully.')
      }).catch(error => {
        this.deleting = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to delete your account. Please try again.')
      })
    },
    onSaveAccountInformationClicked(): void {
      if (!this.accountInformation) return

      this.saving = true

      const accountInformationMessage = new AccountInformationMessage(this.accountInformation.firstName, this.accountInformation.lastName, this.accountInformation.phoneNumber, this.accountInformation.addressLine1, this.accountInformation.addressLine2, this.accountInformation.city, this.accountInformation.stateId, this.accountInformation.postalCode)
      this.miscellaneousService.updateAccountInformation(accountInformationMessage).then(() => {
        this.unchangedAccountInformation = JSON.parse(JSON.stringify(this.accountInformation))
        this.saving = false
        this.toast.success('Your account information was updated successfully.')
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to update your account information. Please try again.')
      })
    }
  }
})
